// frontend/src/pages/Admin.js
import React, { useState } from "react";
import api from "../api";
import { useApp } from "../context/AppContext";
import { useCurrency } from "../hooks/useCurrency";

export default function Admin() {
  const { fmt } = useCurrency();
  const { users, addUser, toggleUserActive } = useApp();
  const [tab, setTab] = useState("users");
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({
    name: "",
    email: "",
    password: "",
    role: "seller",
  });
  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState("");
  const [msgType, setMsgType] = useState("success");

  const handleCreate = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/api/auth/register", form);
      addUser({ ...data, is_active: 1, created_at: new Date().toISOString() });
      setMsg("User created successfully");
      setMsgType("success");
      setShowForm(false);
      setForm({ name: "", email: "", password: "", role: "seller" });
    } catch (err) {
      setMsg(err.response?.data?.error || "Failed to create user");
      setMsgType("error");
    }
    setLoading(false);
    setTimeout(() => setMsg(""), 5000);
  };

  const handleToggle = async (id) => {
    try {
      await api.put(`/api/auth/users/${id}/toggle`);
      toggleUserActive(id);
    } catch (err) {
      alert(err.response?.data?.error || "Failed");
    }
  };

  const roleBadge = {
    manager: "bg-amber-100 text-amber-700",
    seller: "bg-blue-100 text-blue-700",
    buyer: "bg-green-100 text-green-700",
    group: "bg-red-100 text-red-700",
  };

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pb-5 mb-6 border-b border-gray-200">
        <div>
          <div className="text-xl sm:text-2xl font-bold text-gray-900">
            Admin
          </div>
          <div className="text-sm text-gray-400 mt-0.5">
            Manage users &amp; refund requests
          </div>
        </div>
        {tab === "users" && (
          <button
            className="bg-blue-600 hover:bg-blue-700 text-white rounded-lg px-4 py-2 text-sm font-bold transition-colors"
            onClick={() => setShowForm(!showForm)}
          >
            {showForm ? "✕ Cancel" : "+ Add User"}
          </button>
        )}
      </div>

      {/* Tabs */}
      <div className="flex gap-1 mb-5 bg-gray-100 border border-gray-200 rounded-xl p-1 w-fit">
        {[["users", "👥 Users"]].map(([key, label]) => (
          <button
            key={key}
            onClick={() => setTab(key)}
            className={`relative px-4 py-2 rounded-lg text-xs font-semibold transition-all ${tab === key ? "bg-white text-gray-900 shadow-sm" : "text-gray-500 hover:text-gray-700"}`}
          >
            {label}
          </button>
        ))}
      </div>

      {tab === "users" && (
        <>
          {msg && (
            <div
              className={`rounded-lg px-4 py-2.5 text-sm font-medium mb-4 border ${msgType === "success" ? "bg-green-50 text-green-700 border-green-200" : "bg-red-50 text-red-700 border-red-200"}`}
            >
              {msg}
            </div>
          )}

          {showForm && (
            <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5 mb-5">
              <h3 className="text-base font-bold text-gray-900 mb-4">
                Create New User
              </h3>
              <form onSubmit={handleCreate}>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                  {[
                    {
                      key: "name",
                      label: "Full Name *",
                      type: "text",
                      placeholder: "John Doe",
                      required: true,
                    },
                    {
                      key: "email",
                      label: "Email Address *",
                      type: "email",
                      placeholder: "john@iDuuka.com",
                      required: true,
                    },
                    {
                      key: "password",
                      label: "Password * (min 6 chars)",
                      type: "password",
                      minLength: 6,
                      required: true,
                    },
                  ].map(
                    ({
                      key,
                      label,
                      type,
                      placeholder,
                      required,
                      minLength,
                    }) => (
                      <div key={key}>
                        <label className="block text-xs font-semibold text-gray-600 mb-1.5">
                          {label}
                        </label>
                        <input
                          className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
                          type={type}
                          value={form[key]}
                          onChange={(e) =>
                            setForm({ ...form, [key]: e.target.value })
                          }
                          placeholder={placeholder}
                          required={required}
                          minLength={minLength}
                        />
                      </div>
                    ),
                  )}
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-1.5">
                      Role *
                    </label>
                    <select
                      className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm outline-none focus:border-blue-500 bg-white"
                      value={form.role}
                      onChange={(e) =>
                        setForm({ ...form, role: e.target.value })
                      }
                    >
                      <option value="seller">Seller — can sell products</option>
                      <option value="buyer">Buyer — can add stock</option>
                      <option value="manager">Manager — full access</option>
                      <option value="group">
                        Group — owner of several shops
                      </option>
                    </select>
                  </div>
                </div>
                <div className="flex gap-3">
                  <button
                    className="bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white rounded-lg px-5 py-2 text-sm font-bold transition-colors"
                    type="submit"
                    disabled={loading}
                  >
                    {loading ? "Creating…" : "Create User"}
                  </button>
                  <button
                    className="bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg px-4 py-2 text-sm font-semibold transition-colors"
                    type="button"
                    onClick={() => setShowForm(false)}
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          )}

          {/* Role permissions */}
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5 mb-5">
            <h3 className="text-sm font-bold text-gray-900 mb-4">
              Role Permissions
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3">
              {[
                {
                  role: "Manager",
                  emoji: "👔",
                  color: "text-amber-700 bg-amber-50 border-amber-200",
                  perms: [
                    "Full dashboard",
                    "All reports",
                    "Manage users",
                    "Approve / reject refunds",
                    "Cash up / Lift",
                    "Sell products",
                  ],
                },
                {
                  role: "Seller",
                  emoji: "🛍️",
                  color: "text-blue-700 bg-blue-50 border-blue-200",
                  perms: [
                    "Sell products",
                    "Own sales log",
                    "View inventory",
                    "Request refunds",
                  ],
                },
                {
                  role: "Buyer",
                  emoji: "📦",
                  color: "text-green-700 bg-green-50 border-green-200",
                  perms: ["Add stock to inventory", "View inventory"],
                },
                {
                  role: "Group",
                  emoji: "🏢",
                  color: "text-red-700 bg-red-50 border-red-200",
                  perms: [
                    "All manager permissions",
                    "Multi-shop control",
                    "Top-level access",
                  ],
                },
              ].map(({ role, emoji, color, perms }) => (
                <div key={role} className={`rounded-xl p-3.5 border ${color}`}>
                  <div className="font-bold text-sm mb-2.5">
                    {emoji} {role}
                  </div>
                  {perms.map((p) => (
                    <div
                      key={p}
                      className="flex items-start gap-1.5 text-xs mb-1.5 opacity-80"
                    >
                      <span className="mt-0.5 shrink-0">✓</span> {p}
                    </div>
                  ))}
                </div>
              ))}
            </div>
          </div>

          {/* Users table */}
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
            <div className="px-5 py-4 border-b border-gray-100 font-bold text-sm text-gray-900">
              All Users ({users.length})
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm border-collapse">
                <thead className="bg-gray-50">
                  <tr>
                    {[
                      "Name",
                      "Email",
                      "Role",
                      "Status",
                      "Created",
                      "Actions",
                    ].map((h) => (
                      <th
                        key={h}
                        className="text-left px-4 py-3 text-[10.5px] font-semibold uppercase tracking-wide text-gray-400 border-b border-gray-200"
                      >
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {users.map((u) => (
                    <tr
                      key={u.id}
                      className="hover:bg-gray-50 transition-colors"
                    >
                      <td className="px-4 py-3 border-b border-gray-100 font-semibold text-gray-900">
                        {u.name}
                      </td>
                      <td className="px-4 py-3 border-b border-gray-100 text-xs text-gray-500">
                        {u.email}
                      </td>
                      <td className="px-4 py-3 border-b border-gray-100">
                        <span
                          className={`inline-block rounded-full px-2.5 py-0.5 text-xs font-semibold ${roleBadge[u.role] || "bg-blue-100 text-blue-700"}`}
                        >
                          {u.role}
                        </span>
                      </td>
                      <td className="px-4 py-3 border-b border-gray-100">
                        <span
                          className={`inline-block rounded-full px-2.5 py-0.5 text-xs font-semibold ${u.is_active ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"}`}
                        >
                          {u.is_active ? "Active" : "Inactive"}
                        </span>
                      </td>
                      <td className="px-4 py-3 border-b border-gray-100 text-xs text-gray-400">
                        {new Date(u.created_at).toLocaleDateString("en-GB")}
                      </td>
                      <td className="px-4 py-3 border-b border-gray-100">
                        <button
                          onClick={() => handleToggle(u.id)}
                          className={`rounded-md px-3 py-1 text-xs font-semibold transition-colors ${u.is_active ? "bg-red-50 text-red-700 border border-red-200 hover:bg-red-100" : "bg-green-50 text-green-700 border border-green-200 hover:bg-green-100"}`}
                        >
                          {u.is_active ? "Deactivate" : "Activate"}
                        </button>
                      </td>
                    </tr>
                  ))}
                  {users.length === 0 && (
                    <tr>
                      <td
                        colSpan="6"
                        className="text-center text-gray-400 py-10"
                      >
                        No users found
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
